import type {
  AdminApiCreditUsage,
  AdminApiUsagePeriod,
  PurchasableCreditType,
} from "@repo/app-types/entities/creditUsage";
import {
  MetronomeErrorCode,
  metronomeErrorResponseSchema,
} from "@repo/app-types/entities/metronome";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { DropdownMenu } from "@repo/ui/Dropdown/dropdownMenu";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { Size } from "@repo/ui/helpers/helpers";
import { IconCalendar, IconCreditCard } from "@tabler/icons-react";
import { useState, type FC, type ReactNode } from "react";
import { MenuTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { isFetchBaseQueryError } from "../../../api/helpers/errors.js";
import { PurchaseCreditPackModal } from "./purchaseCreditPackModal.js";
import { formatPeriodLabel } from "./usageFormatting.js";

type MetronomeErrorCodeValue =
  (typeof MetronomeErrorCode)[keyof typeof MetronomeErrorCode];

/**
 * The state every credit section derives from its usage queries. The
 * current-period query doubles as the section shell (period list), so
 * switching periods re-renders the body without blanking the picker.
 */
export interface CreditUsageSectionState<TUsage extends AdminApiCreditUsage> {
  currentUsage: TUsage | undefined;
  selectedUsage: TUsage | undefined;
  usageErrorCode: MetronomeErrorCodeValue | null;
  isUsageError: boolean;
  isUsageLoading: boolean;
}

/**
 * `currentData` is intentionally argument-specific. RTK Query's `data` falls
 * back to the previous argument's result while a new period loads, which
 * would make stale usage look like it belongs to the selected period.
 */
export function useCreditUsageSection<TUsage extends AdminApiCreditUsage>(
  useUsageQuery: (args: { periodOffset: number }) => {
    currentData: TUsage | undefined;
    error?: unknown;
    isError: boolean;
    isFetching: boolean;
  },
  periodOffset: number,
): CreditUsageSectionState<TUsage> {
  const { currentData: currentUsage } = useUsageQuery({ periodOffset: 0 });
  const {
    currentData: selectedUsage,
    error: usageQueryError,
    isError: isUsageError,
    isFetching,
  } = useUsageQuery({ periodOffset });
  const parsedUsageError = isFetchBaseQueryError(usageQueryError)
    ? metronomeErrorResponseSchema.safeParse(usageQueryError.data)
    : null;
  return {
    currentUsage,
    selectedUsage,
    usageErrorCode: parsedUsageError?.success
      ? parsedUsageError.data.errorCode
      : null,
    isUsageError,
    isUsageLoading:
      !isUsageError && (isFetching || selectedUsage === undefined),
  };
}

interface CreditUsageHeaderProps {
  /** Periods offered by the picker; hidden until the current period loads. */
  periods: readonly AdminApiUsagePeriod[] | undefined;
  periodOffset: number;
  onPeriodOffsetChange: (periodOffset: number) => void;
  /** Credit types the company can buy packs of. */
  purchasableCreditTypes: readonly PurchasableCreditType[];
}

const PURCHASE_LABEL_BY_CREDIT_TYPE: Record<PurchasableCreditType, string> = {
  ai_credits: "AI credits",
};

/**
 * The page-level header shared by every credit section: title, one period
 * picker, and one purchase button. Both credit types report the same billing
 * periods, so the AI usage's period list stands for all of them. With more
 * than one purchasable type the button opens a menu to choose which.
 */
export const CreditUsageHeader: FC<CreditUsageHeaderProps> = (props) => {
  const {
    periods,
    periodOffset,
    onPeriodOffsetChange,
    purchasableCreditTypes,
  } = props;
  const [purchaseCreditType, setPurchaseCreditType] =
    useState<PurchasableCreditType | null>(null);
  const periodOptions: readonly SimpleSelectOption<number>[] =
    periods?.map((period) => ({
      value: period.offset,
      label: formatPeriodLabel(period),
    })) ?? [];
  const [soleCreditType] =
    purchasableCreditTypes.length === 1 ? purchasableCreditTypes : [];

  const purchaseButton = (
    <Button
      icon={<IconCreditCard className="size-4" />}
      onClick={
        soleCreditType === undefined
          ? undefined
          : () => setPurchaseCreditType(soleCreditType)
      }
      size={Size.SMALL}
      variant={ButtonVariant.Primary}
    >
      Buy credits
    </Button>
  );

  return (
    <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
      <h2 className="text-base font-semibold text-foreground">
        Usage and credits
      </h2>
      <div className="flex items-center gap-2">
        {periods ? (
          <SimpleSelect
            isLabelHidden
            label="Usage period"
            leftAccessory={<IconCalendar className="size-4" />}
            onChange={onPeriodOffsetChange}
            options={periodOptions}
            size={Size.SMALL}
            value={periodOffset}
          />
        ) : null}
        {purchasableCreditTypes.length === 0 ? null : soleCreditType !==
          undefined ? (
          purchaseButton
        ) : (
          <MenuTrigger>
            {purchaseButton}
            <DropdownMenu
              items={purchasableCreditTypes.map((creditType) => ({
                label: PURCHASE_LABEL_BY_CREDIT_TYPE[creditType],
                onClick: () => setPurchaseCreditType(creditType),
              }))}
            />
          </MenuTrigger>
        )}
      </div>
      {purchaseCreditType ? (
        <PurchaseCreditPackModal
          creditType={purchaseCreditType}
          currentPeriodEnd={
            periods?.find((period) => period.offset === 0)?.ends_at
          }
          isOpen
          onClose={() => setPurchaseCreditType(null)}
        />
      ) : null}
    </div>
  );
};

interface CreditUsageStatusProps {
  state: CreditUsageSectionState<AdminApiCreditUsage>;
  /** Shown for a period Metronome metered nothing in. */
  noDataMessage: string;
  loadErrorTitle: string;
  /** The loaded state. */
  children: ReactNode;
}

/**
 * Metronome metering nothing for this scope — a company whose billing is not
 * set up yet, or a period that closed before the company was on a Metronome
 * contract — is not a failure, so it is not announced as one; an unreadable
 * Metronome is the alert.
 */
export const CreditUsageStatus: FC<CreditUsageStatusProps> = (props) => {
  const { state, noDataMessage, loadErrorTitle, children } = props;

  if (state.usageErrorCode === MetronomeErrorCode.NOT_FOUND) {
    return (
      <div
        role="status"
        className="rounded-md border border-border-light bg-white px-5 py-4 text-sm text-muted-foreground"
      >
        {noDataMessage}
      </div>
    );
  }
  if (state.usageErrorCode === MetronomeErrorCode.METRONOME_UNAVAILABLE) {
    return (
      <div role="alert">
        <InfoBanner
          title="Billing is unavailable"
          description="Try again later."
          variant={SimpleModalVariant.Warning}
        />
      </div>
    );
  }
  if (state.isUsageError) {
    return (
      <div role="alert">
        <InfoBanner
          title={loadErrorTitle}
          description="Refresh the page to try again."
          variant={SimpleModalVariant.Warning}
        />
      </div>
    );
  }
  if (state.isUsageLoading) {
    return (
      <div className="rounded-md border border-border-light bg-white px-5 py-4">
        <DefaultSkeleton />
      </div>
    );
  }
  return state.selectedUsage ? children : null;
};

interface UsageBarProps {
  percentage: number;
  /**
   * Whether Metronome left any of this period's usage uncovered. A closed
   * period can pass 100% of its included grant with a purchased pack having
   * covered the rest, so the alarm state follows the overage figure rather
   * than the bar's own fill.
   */
  isUncovered: boolean;
  className?: string;
}

/** Credits consumed against the period's spend capacity. */
export const UsageBar: FC<UsageBarProps> = (props) => {
  const { percentage, isUncovered, className } = props;

  const fillColor = isUncovered
    ? "bg-red-500"
    : percentage >= 80
      ? "bg-amber-500"
      : "bg-indigo-600";

  return (
    <div
      aria-label={`${Math.round(percentage)}% of available credits used`}
      className={twMerge(
        "h-2 overflow-hidden rounded-full bg-gray-100",
        className,
      )}
      role="meter"
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.min(100, Math.round(percentage))}
    >
      <div
        className={twMerge(
          "h-full rounded-full transition-all duration-300",
          fillColor,
        )}
        style={{ width: `${Math.min(100, percentage)}%` }}
      />
    </div>
  );
};
